import { useNavigate } from 'react-router-dom';
import { ChevronDown, LogOut, Settings } from 'lucide-react';
import { useLogout } from '@/features/auth/use-logout';
import { ROUTES } from '@/routes/paths';
import { selectActiveTenant, selectUser, useAuthStore } from '@/stores/auth-store';
import { roleLabel } from '@/theme';
import { cn } from '@/lib/utils';
import { Avatar } from '@/components/ui/Avatar';
import { Dropdown, DropdownItem, DropdownSeparator } from '@/components/ui/Dropdown';

/**
 * Bloque de usuario del header (foto + nombre + rol, igual que en la referencia).
 * El menú ofrece ir a Ajustes y cerrar sesión.
 */
export function UserMenu() {
  const user = useAuthStore(selectUser);
  const activeTenant = useAuthStore(selectActiveTenant);
  const logout = useLogout();
  const navigate = useNavigate();

  if (!user) return null;

  const displayName = user.nombre?.trim() || user.email;
  const tenantRole = activeTenant ? roleLabel[activeTenant.role] : 'Sin negocio';

  return (
    <Dropdown
      className="w-60"
      trigger={({ open, toggle }) => (
        <button
          type="button"
          onClick={toggle}
          aria-haspopup="menu"
          aria-expanded={open}
          className="flex items-center gap-2.5 rounded-control px-1.5 py-1 transition-colors hover:bg-surface-muted"
        >
          <Avatar name={displayName} size="md" />

          <span className="hidden text-left sm:block">
            <span className="block max-w-40 truncate text-caption font-semibold text-ink">
              {displayName}
            </span>
            <span className="block max-w-40 truncate text-micro text-ink-faint">
              {tenantRole}
            </span>
          </span>

          <ChevronDown
            size={16}
            className={cn(
              'hidden shrink-0 text-ink-faint transition-transform sm:block',
              open && 'rotate-180',
            )}
          />
        </button>
      )}
    >
      {({ close }) => (
        <>
          <div className="px-3 py-2">
            <p className="truncate text-caption font-semibold text-ink">{displayName}</p>
            <p className="truncate text-micro text-ink-faint">{user.email}</p>
          </div>

          <DropdownSeparator />

          <DropdownItem
            icon={<Settings size={16} />}
            onClick={() => {
              close();
              navigate(ROUTES.settings);
            }}
          >
            Ajustes del negocio
          </DropdownItem>

          <DropdownItem
            icon={<LogOut size={16} />}
            tone="danger"
            onClick={() => {
              close();
              logout();
            }}
          >
            Cerrar sesión
          </DropdownItem>
        </>
      )}
    </Dropdown>
  );
}
